import { useEffect, useState } from 'react';
import { authApi, superAdminApi } from '../../api';
import { googleOAuthApi } from '../../api/googleOAuthApi';
import { toast } from 'sonner';
import {
  AlertTriangle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Copy,
  ExternalLink,
  Eye,
  EyeOff,
  Globe,
  KeyRound,
  Loader2,
  MapPin,
  Save,
  ShieldCheck,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';

const GOOGLE_SETUP_STEPS = [
  'Open Google Cloud Console and create or select the CredBloom production project.',
  'Enable My Business Account Management API, My Business Business Information API, Business Profile Performance API, and Places API (New).',
  'Request Google Business Profile API access for the project if it has not already been approved.',
  'Configure OAuth consent screen as External, use CredBloom as the app name, and add itsmmp.com as an authorized domain.',
  'Add the scope https://www.googleapis.com/auth/business.manage and complete Google OAuth app verification for production users.',
  'Create Credentials → OAuth client ID → Web application.',
  'Add the exact Authorized Redirect URI shown below. Do not add a trailing slash.',
  'Copy the Client ID and Client Secret into this card and save.',
];

const GOOGLE_ERRORS = [
  {
    title: 'redirect_uri_mismatch',
    text: 'The URI in Google Cloud must exactly match the URI saved here, including https, domain, path, and trailing slash.',
  },
  {
    title: 'Access blocked / unverified app',
    text: 'Complete the OAuth consent screen, add required test users during testing, or submit the app for Google verification before public use.',
  },
  {
    title: 'No Business Profile found',
    text: 'The client must sign in with a Google account that is an Owner or Manager of a verified Business Profile.',
  },
  {
    title: 'API not enabled / permission denied',
    text: 'Enable the listed Business Profile APIs in the same project that owns this OAuth Client ID, then wait a few minutes and retry.',
  },
  {
    title: 'Token expired or revoked',
    text: 'The client can use Reconnect from Platforms. Existing access is replaced safely and the selected business can be chosen again.',
  },
  {
    title: 'Quota exceeded',
    text: 'Wait about one minute before retrying. For production volume, request higher quota from Google Cloud Console.',
  },
];

function StatusLine({ ok, children }) {
  return (
    <div className={`flex items-center gap-2 text-sm ${ok ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400'}`}>
      {ok ? <CheckCircle size={14} /> : <AlertTriangle size={14} />}
      {children}
    </div>
  );
}

function GoogleOAuthCard() {
  const defaultRedirect = `${window.location.origin}/admin/oauth/callback`;
  const [status, setStatus] = useState({
    configured: false,
    client_id_set: false,
    client_id_masked: null,
    client_secret_set: false,
    redirect_uri: '',
  });
  const [form, setForm] = useState({ client_id: '', client_secret: '', redirect_uri: defaultRedirect });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [stepsOpen, setStepsOpen] = useState(true);
  const [errorsOpen, setErrorsOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await googleOAuthApi.getAdminConfig();
      setStatus(data);
      setForm((current) => ({
        ...current,
        redirect_uri: data.redirect_uri || defaultRedirect,
      }));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load Google OAuth configuration');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    const payload = {};
    if (form.client_id.trim()) payload.client_id = form.client_id.trim();
    if (form.client_secret.trim()) payload.client_secret = form.client_secret.trim();
    if ((form.redirect_uri || '').trim() !== (status.redirect_uri || '').trim()) {
      payload.redirect_uri = form.redirect_uri.trim();
    }
    if (!status.redirect_uri && form.redirect_uri.trim()) payload.redirect_uri = form.redirect_uri.trim();

    if (Object.keys(payload).length === 0) {
      return toast.info('Enter a new value or update the redirect URI');
    }

    setSaving(true);
    try {
      const { data } = await googleOAuthApi.updateAdminConfig(payload);
      setStatus(data);
      setForm({ client_id: '', client_secret: '', redirect_uri: data.redirect_uri || defaultRedirect });
      toast.success(data.configured ? 'Google OAuth is ready for clients' : 'Google OAuth settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Google OAuth settings');
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!window.confirm('Clear the global Google OAuth Client ID, Client Secret, and Redirect URI? Existing client tokens will remain stored but reconnect will not work until configuration is restored.')) return;
    setSaving(true);
    try {
      const { data } = await googleOAuthApi.updateAdminConfig({
        client_id: '',
        client_secret: '',
        redirect_uri: '',
      });
      setStatus(data);
      setForm({ client_id: '', client_secret: '', redirect_uri: defaultRedirect });
      toast.success('Google OAuth configuration cleared');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to clear configuration');
    } finally {
      setSaving(false);
    }
  };

  const copyRedirect = async () => {
    try {
      await navigator.clipboard.writeText(form.redirect_uri || defaultRedirect);
      toast.success('Authorized Redirect URI copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  return (
    <Card data-testid="sa-google-oauth-card" className="border-blue-200 dark:border-blue-900">
      <CardHeader className="pb-3">
        <CardTitle className="flex flex-wrap items-center justify-between gap-3 text-base">
          <span className="flex items-center gap-2">
            <ShieldCheck size={17} className="text-blue-600" /> Google Business Profile OAuth
          </span>
          <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${status.configured ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'}`}>
            {status.configured ? 'Ready for clients' : 'Setup required'}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="rounded-lg border border-blue-100 bg-blue-50 p-3 text-xs leading-relaxed text-blue-800 dark:border-blue-900 dark:bg-blue-950/30 dark:text-blue-200">
          Configure one CredBloom-owned Google OAuth application here. Clients will never see or enter developer credentials; they will only sign in to Google, approve access, and select their Business Profile.
        </div>

        {loading ? (
          <Loader2 className="animate-spin text-muted-foreground" size={18} />
        ) : (
          <>
            <div className="grid gap-2 sm:grid-cols-3">
              <StatusLine ok={status.client_id_set}>Client ID {status.client_id_masked ? `(${status.client_id_masked})` : 'not set'}</StatusLine>
              <StatusLine ok={status.client_secret_set}>Client Secret {status.client_secret_set ? 'saved' : 'not set'}</StatusLine>
              <StatusLine ok={Boolean(status.redirect_uri)}>Redirect URI {status.redirect_uri ? 'saved' : 'not set'}</StatusLine>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">{status.client_id_set ? 'Replace Google OAuth Client ID' : 'Google OAuth Client ID'}</Label>
              <Input
                value={form.client_id}
                onChange={(event) => setForm((value) => ({ ...value, client_id: event.target.value }))}
                placeholder={status.client_id_set ? 'Leave blank to keep the existing Client ID' : '1234567890-abc.apps.googleusercontent.com'}
                className="font-mono text-xs"
                data-testid="sa-google-oauth-client-id"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">{status.client_secret_set ? 'Replace Google OAuth Client Secret' : 'Google OAuth Client Secret'}</Label>
              <div className="relative">
                <Input
                  type={showSecret ? 'text' : 'password'}
                  value={form.client_secret}
                  onChange={(event) => setForm((value) => ({ ...value, client_secret: event.target.value }))}
                  placeholder={status.client_secret_set ? 'Leave blank to keep the existing secret' : 'GOCSPX-...'}
                  className="pr-10 font-mono text-xs"
                  autoComplete="new-password"
                  data-testid="sa-google-oauth-client-secret"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret((value) => !value)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground"
                  aria-label="Toggle Client Secret visibility"
                >
                  {showSecret ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              <p className="text-[11px] text-muted-foreground">Stored encrypted in MongoDB and never returned to the browser.</p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Authorized Redirect URI</Label>
              <div className="flex flex-col gap-2 sm:flex-row">
                <Input
                  value={form.redirect_uri}
                  onChange={(event) => setForm((value) => ({ ...value, redirect_uri: event.target.value }))}
                  className="font-mono text-xs"
                  data-testid="sa-google-oauth-redirect-uri"
                />
                <Button type="button" variant="outline" onClick={copyRedirect} className="gap-2 sm:shrink-0">
                  <Copy size={13} /> Copy
                </Button>
              </div>
              <p className="text-[11px] text-amber-700 dark:text-amber-400">Use this exact value in Google Cloud. No trailing slash, query string, or alternate domain.</p>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button onClick={save} disabled={saving} className="gap-2" data-testid="sa-google-oauth-save">
                {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                Save Google OAuth
              </Button>
              <a href="https://console.cloud.google.com/apis/credentials" target="_blank" rel="noreferrer">
                <Button type="button" variant="outline" className="gap-2">
                  <ExternalLink size={13} /> Google Cloud Credentials
                </Button>
              </a>
              {(status.client_id_set || status.client_secret_set || status.redirect_uri) && (
                <Button type="button" variant="outline" onClick={clear} disabled={saving} className="text-destructive">
                  Clear configuration
                </Button>
              )}
            </div>
          </>
        )}

        <div className="rounded-lg border">
          <button type="button" className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-medium" onClick={() => setStepsOpen((value) => !value)}>
            Google Cloud configuration steps
            {stepsOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {stepsOpen && (
            <ol className="space-y-3 border-t px-4 py-4 text-xs leading-relaxed text-muted-foreground">
              {GOOGLE_SETUP_STEPS.map((step, index) => (
                <li key={step} className="flex gap-3">
                  <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary/10 text-[10px] font-bold text-primary">{index + 1}</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
          )}
        </div>

        <div className="rounded-lg border border-amber-200 dark:border-amber-900">
          <button type="button" className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-medium" onClick={() => setErrorsOpen((value) => !value)}>
            Common errors and fixes
            {errorsOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
          {errorsOpen && (
            <div className="grid gap-3 border-t px-4 py-4 sm:grid-cols-2">
              {GOOGLE_ERRORS.map((item) => (
                <div key={item.title} className="rounded-lg bg-muted/40 p-3">
                  <p className="text-xs font-semibold">{item.title}</p>
                  <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">{item.text}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function GlobalPlacesKeyCard() {
  const [status, setStatus] = useState({ google_places_api_key_set: false, google_places_api_key_masked: null });
  const [newKey, setNewKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showKey, setShowKey] = useState(false);

  const load = async () => {
    try {
      const { data } = await superAdminApi.getGlobalSettings();
      setStatus(data);
    } catch {
      toast.error('Failed to load global settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!newKey.trim()) return;
    setSaving(true);
    try {
      await superAdminApi.updateGlobalSettings({ google_places_api_key: newKey.trim() });
      setNewKey('');
      await load();
      toast.success('Places API key saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save Places API key');
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    if (!window.confirm('Clear the global Places API key?')) return;
    setSaving(true);
    try {
      await superAdminApi.updateGlobalSettings({ google_places_api_key: '' });
      await load();
      toast.success('Places API key cleared');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base"><MapPin size={16} /> Global Google Places API Key</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs leading-relaxed text-muted-foreground">Used for public business search, profile details, competitors, and public review statistics. It is separate from the Google Business OAuth credentials above.</p>
        {loading ? <Loader2 className="animate-spin text-muted-foreground" size={16} /> : (
          <>
            <StatusLine ok={status.google_places_api_key_set}>
              {status.google_places_api_key_set ? `Configured (${status.google_places_api_key_masked})` : 'No global Places API key configured'}
            </StatusLine>
            <div className="relative">
              <Input type={showKey ? 'text' : 'password'} value={newKey} onChange={(event) => setNewKey(event.target.value)} placeholder={status.google_places_api_key_set ? 'Leave blank to keep existing key' : 'AIza…'} className="pr-10 font-mono text-xs" />
              <button type="button" onClick={() => setShowKey((value) => !value)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
              </button>
            </div>
            <div className="flex gap-2">
              <Button onClick={save} disabled={saving || !newKey.trim()} className="gap-2"><Save size={14} /> Save</Button>
              {status.google_places_api_key_set && <Button variant="outline" onClick={clear} disabled={saving} className="text-destructive">Clear</Button>}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function GoDaddyCard() {
  const [status, setStatus] = useState({ godaddy_api_key_set: false, godaddy_api_key_masked: null, godaddy_api_secret_set: false, godaddy_apex_domain: '' });
  const [form, setForm] = useState({ apex: '', key: '', secret: '' });
  const [show, setShow] = useState({ key: false, secret: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await superAdminApi.getGlobalSettings();
      setStatus(data);
      setForm((value) => ({ ...value, apex: data.godaddy_apex_domain || '' }));
    } catch {
      toast.error('Failed to load GoDaddy settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    const payload = {};
    if (form.key.trim()) payload.godaddy_api_key = form.key.trim();
    if (form.secret.trim()) payload.godaddy_api_secret = form.secret.trim();
    if (form.apex.trim() !== (status.godaddy_apex_domain || '')) payload.godaddy_apex_domain = form.apex.trim();
    if (!Object.keys(payload).length) return toast.info('Nothing to update');
    setSaving(true);
    try {
      await superAdminApi.updateGlobalSettings(payload);
      setForm((value) => ({ ...value, key: '', secret: '' }));
      await load();
      toast.success('GoDaddy settings saved');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save GoDaddy settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader className="pb-2"><CardTitle className="flex items-center gap-2 text-base"><Globe size={16} /> GoDaddy — Subdomain Automation</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs leading-relaxed text-muted-foreground">Stores production GoDaddy credentials for customer website DNS automation. This is unrelated to the credbloom.itsmmp.com Render custom domain.</p>
        {loading ? <Loader2 className="animate-spin" size={16} /> : (
          <>
            <Input value={form.apex} onChange={(event) => setForm((value) => ({ ...value, apex: event.target.value.toLowerCase() }))} placeholder="example.com" className="font-mono text-xs" />
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="relative">
                <Input type={show.key ? 'text' : 'password'} value={form.key} onChange={(event) => setForm((value) => ({ ...value, key: event.target.value }))} placeholder={status.godaddy_api_key_set ? `Configured ${status.godaddy_api_key_masked || ''}` : 'API Key'} className="pr-9 font-mono text-xs" />
                <button type="button" onClick={() => setShow((value) => ({ ...value, key: !value.key }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">{show.key ? <EyeOff size={14} /> : <Eye size={14} />}</button>
              </div>
              <div className="relative">
                <Input type={show.secret ? 'text' : 'password'} value={form.secret} onChange={(event) => setForm((value) => ({ ...value, secret: event.target.value }))} placeholder={status.godaddy_api_secret_set ? 'Secret configured' : 'API Secret'} className="pr-9 font-mono text-xs" />
                <button type="button" onClick={() => setShow((value) => ({ ...value, secret: !value.secret }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">{show.secret ? <EyeOff size={14} /> : <Eye size={14} />}</button>
              </div>
            </div>
            <Button onClick={save} disabled={saving} className="gap-2">{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save</Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function ChangePasswordCard() {
  const [form, setForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [loading, setLoading] = useState(false);

  const submit = async (event) => {
    event.preventDefault();
    if (!form.current_password || !form.new_password || !form.confirm_password) return toast.error('All password fields are required');
    if (form.new_password !== form.confirm_password) return toast.error('New passwords do not match');
    if (form.new_password.length < 6) return toast.error('Password must be at least 6 characters');
    setLoading(true);
    try {
      await authApi.changePassword(form);
      setForm({ current_password: '', new_password: '', confirm_password: '' });
      toast.success('Password updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update password');
    } finally {
      setLoading(false);
    }
  };

  const field = (name, label, showKey) => (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="relative">
        <Input type={show[showKey] ? 'text' : 'password'} value={form[name]} onChange={(event) => setForm((value) => ({ ...value, [name]: event.target.value }))} className="pr-10" />
        <button type="button" onClick={() => setShow((value) => ({ ...value, [showKey]: !value[showKey] }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">{show[showKey] ? <EyeOff size={14} /> : <Eye size={14} />}</button>
      </div>
    </div>
  );

  return (
    <Card className="max-w-md">
      <CardHeader className="pb-3"><CardTitle className="flex items-center gap-2 text-base"><KeyRound size={16} /> Change Password</CardTitle></CardHeader>
      <CardContent>
        <form onSubmit={submit} className="space-y-4">
          {field('current_password', 'Current Password', 'current')}
          {field('new_password', 'New Password', 'next')}
          {field('confirm_password', 'Confirm New Password', 'confirm')}
          <Button type="submit" disabled={loading} className="w-full gap-2">{loading ? <Loader2 size={14} className="animate-spin" /> : <KeyRound size={14} />} Update Password</Button>
        </form>
      </CardContent>
    </Card>
  );
}

export default function SASettings() {
  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6 sm:p-8">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope' }}>Settings</h1>
        <p className="text-sm text-muted-foreground">Manage CredBloom-wide integrations and your Super Admin account.</p>
      </div>
      <GoogleOAuthCard />
      <GlobalPlacesKeyCard />
      <GoDaddyCard />
      <ChangePasswordCard />
    </div>
  );
}
